import _ from 'lodash';
import Vue from 'vue';

import { getSourceConfigurationBySourceName } from '@/constants/sourcesConfiguration';
import Segment from '../../Segment';
import CredentialsApi from '../../api/CredentialsApi';
import DDLogs from '../../DDLogs';

const state = {
  credentialsList: [],
  credsToAccounts: [],
  sourceType: null,
  credentialsValid: true,
  credentialsSaveError: false,
  isLoading: false,
};

const getters = {
  credsBySourceType: state => (state.sourceType ? _.filter(state.credentialsList, { type: state.sourceType }) : state.credentialsList),
  invalidCredentialsList: (state, getters) => _.filter(getters.credsBySourceType, { status: 'passwordInvalid' }),
  validCredentialsList: (state, getters) => _.filter(getters.credsBySourceType, { status: 'valid' }),
  unvalidatedCredentialsList: (state, getters) => _.filter(getters.credsBySourceType, { status: 'unvalidated' }),
  partiallyAuthorizedCredentialsList: (state, getters) => _.filter(getters.credsBySourceType, { status: 'partiallyAuthorized' }),
  invalidCredentials: (state, getters) => getters.invalidCredentialsList.length,
  hasCredentials: (state, getters) => getters.credsBySourceType.length > 0,
  connectedCredentials: (state, getters) => [...getters.validCredentialsList, ...getters.unvalidatedCredentialsList,
    ...getters.partiallyAuthorizedCredentialsList],
  hasValidationErrors: (state, getters) => _.some(getters.credsBySourceType, creds => creds.updateError || creds.validations.nameError),
  accountsList: _getAccountsList,
};

const actions = {
  async createCredentials({ commit }, { sourceName }) {
    const { credentials } = await CredentialsApi.createCredentials({ sourceName });
    commit('addCredentialsToCredentialsList', credentials);
    return credentials.credentialsId;
  },
  async deleteCredentials({ commit }, { credentialsId }) {
    await CredentialsApi.deleteCredentials({ credentialsId });
  },
  async fetchCredsToAccounts({ commit }) {
    const credsToAccounts = await CredentialsApi.fetchCredsToAccounts();
    commit('setCredsToAccounts', credsToAccounts);
  },
  async fetchCredentials({ commit, state, dispatch }) {
    const rawCredentialsList = await CredentialsApi.fetchCredentials();
    await dispatch('fetchCredsToAccounts');
    const credentialsList = _.map(rawCredentialsList, _.partial(_convertRawCredentials, state.credsToAccounts));
    commit('setCredentialsList', credentialsList);
  },
  async updateCredentials({ commit, state }, { source, credentials, renew = false }) {
    commit('setIsLoading', true);
    const credentialsObject = _.find(state.credentialsList, { credentialsId: source.credentialsId });
    const { safeId, name, sourceName, credentialsId } = credentialsObject;
    commit('resetCredentialsValidation');

    const checkCredsResult = await _checkCredentials(sourceName, credentials, safeId, credentialsId, renew);
    if (checkCredsResult.reason === 'ValidCredentials') {
      return await _tryUpdatingCreds(commit, sourceName, { credentialsId, credentials, name });
    }
    if (checkCredsResult.reason === 'InvalidCredentials') {
      commit('setCredsValid', { credsValid: false });
    } else {
      commit('setCredentialsSaveError');
    }
    Segment.trackUserGot('CredentialsSaveFailed', { sourceName, reason: checkCredsResult.reason });
    commit('setIsLoading', false);
    return { success: false };
  },
  async updateCredentialsName({ commit, state }, { source }) {
    const credentialsObject = _.find(state.credentialsList, { credentialsId: source.credentialsId });
    const { fields: credentialsFields, name, status, sourceName, credentialsId } = credentialsObject;
    const credentials = _extractCredentials(credentialsFields);
    return await _tryUpdatingCreds(commit, sourceName, { credentialsId, credentials, name, status });
  },
  async saveCredentialsNew({ commit, state }, { sourceName, credentials, name }) {
    commit('setIsLoading', true);
    commit('resetCredentialsValidation');
    const checkCredsResult = await _checkCredentials(sourceName, credentials, null, null, false);
    if (checkCredsResult.reason === 'ValidCredentials') {
      return await _trySavingCreds(commit, sourceName, credentials, name);
    }
    if (checkCredsResult.reason === 'InvalidCredentials') {
      commit('setCredsValid', { credsValid: false });
    } else {
      commit('setCredentialsSaveError');
    }
    commit('setIsLoading', false);
    Segment.trackUserGot('CredentialsSaveFailed', { sourceName, reason: checkCredsResult.reason });
    return { success: false };
  },
  async setName({ commit }, { credentialsId, name }) {
    commit('setCredentialsName', { credentialsId, name });
  },
  async setNameError({ commit }, { credentialsId, error }) {
    commit('setNameError', { credentialsId, error });
  },
};

const mutations = {
  setSourceType(state, sourceType) {
    state.sourceType = sourceType;
  },
  setCredentialsList(state, credentialsList) {
    state.credentialsList = credentialsList;
  },
  setCredsToAccounts(state, credsToAccounts) {
    state.credsToAccounts = credsToAccounts;
  },
  addCredentialsToCredentialsList(state, source) {
    state.credentialsList.push(_convertRawCredentials([], source));
  },
  setCredentialsSaveError(state) {
    state.credentialsSaveError = true;
  },
  resetCredentialsValidation(state) {
    state.credentialsSaveError = false;
    state.credentialsValid = true;
  },
  setCredsValid(state, { credsValid }) {
    state.credentialsValid = credsValid;
  },
  setIsLoading(state, isLoading) {
    state.isLoading = isLoading;
  },
  setCredentialsName(state, { credentialsId, name }) {
    const account = _.find(state.credentialsList, { credentialsId });
    Vue.set(account, 'name', name);
  },
  sortCredentials(state) {
    state.credentialsList.sort((a, b) => {
      if (a.status === 'valid' && b.status === 'valid') {
        return 0;
      }
      if (a.status === 'valid') {
        return -1;
      }
      if (b.status === 'valid') {
        return 1;
      }
      return 0;
    });
  },
};

// These are all helper methods
function _getAccountsList(state) {
  return _.chain(state.credentialsList).map(creds => _.map(creds.accounts, account => _getAccountWithExtraParams(creds, account))).flatten().value();
}

function _getAccountWithExtraParams(creds, account) {
  return {
    ...account,
    sourceName: creds.sourceName,
    name: creds.name,
    accounts: [account],
  };
}

async function _checkCredentials(sourceName, credentials, safeId, credentialsId, renew) {
  const result = await _checkCredentialsInternal(sourceName, credentials, safeId, credentialsId, renew);
  if (!_.isNil(credentialsId)) {
    CredentialsApi.logCredsCheck(credentialsId, result.isValid, result.reason)
      .catch(err => DDLogs.error('Failed logging creds check result', { err, credentialsId, result }));
  }
  return result;
}

async function _checkCredentialsInternal(sourceName, credentials, safeId, credentialsId, renew) {
  try {
    const result = await CredentialsApi.checkCredentials({ sourceName, credentials, safeId, credentialsId, renew });
    if (result.reason === 'ValidCredentials') {
      return { isValid: true, reason: result.reason };
    }
    return { isValid: false, reason: 'InvalidCredentials' };
  } catch (error) {
    DDLogs.error('Creds check failed with error', { error, credentialsId, sourceName, safeId, renew });
    return { isValid: false, reason: 'RequestFailed' };
  }
}

function _extractCredentials(credentialsFields) {
  return _.chain(credentialsFields)
    .mapValues(val => val.value)
    .omitBy(val => !val)
    .mapValues(val => val.trim())
    .value();
}

async function _tryUpdatingCreds(commit, sourceName, payload) {
  try {
    await CredentialsApi.updateCredentials(payload);
    Segment.trackUserGot('CredentialsSaveSuccess', { sourceName });
    return { success: true };
  } catch (e) {
    if (e.response && e.response.status === 400) {
      commit('setNameError', { credentialsId: payload.credentialsId, error: 'כבר השתמשת בשם הזה' });
      Segment.trackUserGot('CredentialsSaveFailed', { sourceName, reason: 'SourceNameAlreadyExists' });
      return { success: false };
    }
    Segment.trackUserGot('CredentialsSaveFailed', { sourceName, reason: 'UnexpectedException' });
    commit('setUpdateError', { credentialsId: payload.credentialsId });
    commit('setCredentialsSaveError');
    return { success: false };
  } finally {
    commit('setIsLoading', false);
  }
}

async function _trySavingCreds(commit, sourceName, credentials, name) {
  try {
    await CredentialsApi.saveCredentials({ sourceName, credentials, name });
    Segment.trackUserGot('CredentialsSaveSuccess', { sourceName });
    return { success: true };
  } catch (e) {
    if (e.response && e.response.status === 400) {
      commit('setNewNameError', { sourceName, error: 'כבר השתמשת בשם הזה' });
      Segment.trackUserGot('CredentialsSaveFailed', { sourceName, reason: 'SourceNameAlreadyExists' });
      return { success: false };
    }
    Segment.trackUserGot('CredentialsSaveFailed', { sourceName, reason: 'UnexpectedException' });
    commit('setSaveError', { sourceName });
    commit('setCredentialsSaveError');
    return { success: false };
  } finally {
    commit('setIsLoading', false);
  }
}

function _convertRawCredentials(credsToAccounts, rawCredentials) {
  const credToAccounts = _.find(credsToAccounts, credToAccounts => credToAccounts.credentialsId === rawCredentials.credentialsId);
  const sourceFieldsObject = _getSourceFieldsObject(rawCredentials.sourceName);
  return _.assign({}, rawCredentials, sourceFieldsObject, { accounts: credToAccounts && credToAccounts.accountNumberPiiIds });
}

function _getSourceFieldsObject(sourceName) {
  const source = getSourceConfigurationBySourceName(sourceName);
  const fieldsWithValue = _.mapValues(source.fields, fieldValue => _.assign({ value: undefined }, fieldValue));
  return _.assign(
    {},
    source,
    { fields: fieldsWithValue },
  );
}
export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
